import { supabase } from './supabase';
import { BaseService } from './base-service';

/**
 * Interface representing a user account
 */
export interface Account {
  id: string;
  phone?: string;
  email?: string;
  full_name?: string;
  avatar_url?: string;
  company_name?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  user_type?: 'buyer' | 'supplier' | 'both';
  is_verified?: boolean;
  is_admin?: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * Service for managing user accounts
 */
export class AccountService extends BaseService {
  constructor() {
    super(supabase);
  }
  
  /**
   * Get user account by ID
   */
  async getAccount(userId: string): Promise<Account | null> {
    try {
      console.log(`AccountService.getAccount - Searching for user ID: ${userId}`);
      console.log(`AccountService.getAccount - User ID type: ${typeof userId}`);
      
      // First, test if we can access accounts table at all
      const { data: testData, error: testError } = await this.supabase
        .from('accounts')
        .select('id, phone')
        .limit(3);
      
      console.log(`AccountService.getAccount - Test query (first 3 accounts):`, { testData, testError });

      // Test query by phone number (we know this exists)
      const { data: phoneData, error: phoneError } = await this.supabase
        .from('accounts')
        .select('*')
        .eq('phone', '+919884406828');
      
      console.log(`AccountService.getAccount - Phone query (+919884406828):`, { phoneData, phoneError });

      const { data, error } = await this.supabase
        .from('accounts')
        .select('*')
        .eq('id', userId);

      console.log(`AccountService.getAccount - Query result:`, { data, error });
      console.log(`AccountService.getAccount - Data length:`, data?.length);
      console.log(`AccountService.getAccount - Raw data:`, data);
      console.log(`AccountService.getAccount - Error details:`, error);

      if (error) {
        console.error(`AccountService.getAccount - Database error:`, error);
        this.handleError(error, 'fetching user account');
        return null;
      }

      // Handle case where no account exists
      if (!data || data.length === 0) {
        console.log(`AccountService.getAccount - No account found for user ID: ${userId}`);
        return null;
      }

      // Handle case where multiple accounts exist (shouldn't happen, but defensive)
      if (data.length > 1) {
        console.warn(`AccountService.getAccount - Multiple accounts found for user ID: ${userId}, using first one`);
        return data[0];
      }

      console.log(`AccountService.getAccount - Found account:`, data[0]);
      return data[0];
    } catch (error) {
      console.error('AccountService.getAccount - Unexpected error:', error);
      return null;
    }
  }

  /**
   * Debug method to test RLS issues
   * This bypasses the normal query to test if RLS is the issue
   */
  async debugAccountAccess(userId: string): Promise<any> {
    try {
      console.log(`AccountService.debugAccountAccess - Testing RLS for user ID: ${userId}`);
      
      // Test 1: Try without any filters (should show RLS working)
      const { data: allData, error: allError } = await this.supabase
        .from('accounts')
        .select('id, phone, full_name')
        .limit(5);
      
      console.log(`AccountService.debugAccountAccess - All accounts query:`, { 
        count: allData?.length, 
        data: allData,
        error: allError 
      });

      // Test 2: Try with RPC function if available
      const { data: rpcData, error: rpcError } = await this.supabase
        .rpc('get_current_user_id');
      
      console.log(`AccountService.debugAccountAccess - Current user RPC:`, { 
        rpcData, 
        rpcError 
      });

      // Test 3: Check auth status
      const { data: { user } } = await this.supabase.auth.getUser();
      console.log(`AccountService.debugAccountAccess - Auth user:`, user?.id);

      return {
        allAccountsVisible: allData?.length || 0,
        currentAuthUser: user?.id,
        rpcResult: rpcData
      };
    } catch (error) {
      console.error('AccountService.debugAccountAccess - Error:', error);
      return null;
    }
  }
  
  /**
   * Get an account by phone number
   * @param phone The phone number
   * @returns The account or null if not found
   */
  async getAccountByPhone(phone: string): Promise<Account | null> {
    try {
      const { data, error } = await this.supabase
        .from('accounts')
        .select('*')
        .eq('phone', phone);
        
      if (error) {
        this.handleError(error, 'fetching account by phone');
        return null;
      }

      // Handle case where no account exists
      if (!data || data.length === 0) {
        return null;
      }

      return data[0];
    } catch (error) {
      this.handleError(error, 'fetching account by phone');
      return null;
    }
  }
  
  /**
   * Create a new account
   * @param account The account data
   * @returns The created account or null if creation failed
   */
  async createAccount(account: Omit<Account, 'id' | 'created_at' | 'updated_at'>): Promise<Account | null> {
    try {
      const accountWithTimestamps = this.addTimestamps(account, true);
      
      const { data, error } = await this.supabase
        .from('accounts')
        .insert(accountWithTimestamps)
        .select()
        .single();
        
      if (error) {
        this.handleError(error, 'creating account');
        return null;
      }
      return data;
    } catch (error) {
      this.handleError(error, 'creating account');
      return null;
    }
  }
  
  /**
   * Update user account
   */
  async updateAccount(userId: string, accountData: Partial<Account>): Promise<Account | null> {
    try {
      const { data, error } = await this.supabase
        .from('accounts')
        .update({
          ...accountData,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select();

      if (error) {
        this.handleError(error, 'updating user account');
        return null;
      }

      // Handle case where no rows were updated (user doesn't exist)
      if (!data || data.length === 0) {
        console.warn(`No account found to update for user ID: ${userId}`);
        return null;
      }

      return data[0];
    } catch (error) {
      console.error('Error in updateAccount:', error);
      return null;
    }
  }
  
  /**
   * Get all accounts (admin only)
   */
  async getAllAccounts(): Promise<Account[]> {
    try {
      const { data, error } = await this.supabase
        .from('accounts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        this.handleError(error, 'fetching all accounts');
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getAllAccounts:', error);
      return [];
    }
  }
  
  /**
   * Delete user account
   */
  async deleteAccount(userId: string): Promise<boolean> {
    try {
      const { error } = await this.supabase
        .from('accounts')
        .delete()
        .eq('id', userId);

      if (error) {
        this.handleError(error, 'deleting user account');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in deleteAccount:', error);
      return false;
    }
  }
}

// Legacy exports for backward compatibility during transition
export type Profile = Account;
export const ProfileService = AccountService;
